import { mergeDeepRight } from 'ramda';
import session from '../../lib/session';
import settings from '../../config';
import { broadcast_reload_message } from '../../lib/synchronize';
import { subdomain_from, url_for } from '../../lib/utils';
import { unauthorized_endpoints, api_request } from './api-request';
import { init_translations, t9n } from '../../lib/translate';
import router from '../../lib/router';

// branding ---------------------------------------------------------------- //
const default_branding = {
  orderer_background_color: '#006699',
  orderer_foreground_color: 'white',
  orderer_background_image_url: '/assets/orderer_background1.jpg',

  worker_background_color: '#ff9800',
  worker_foreground_color: 'black',
  worker_background_image_url: '/assets/worker_background1.jpg',

  logo_url: '/assets/worker-logo.png',

  list_item_color: '#ff9900',

  home_hexa_pilot: '#006699',
  home_hexa_apps: '#FF9900',
  home_hexa_ongoing: '#009999',

  home_hexa_pilot_hover: '#007bb5',
  home_hexa_apps_hover: '#ffaa2b',
  home_hexa_ongoing_hover: '#00a8a8',

  shared_order_header_color: '#ff9800',
  shared_order_header_line_color: '#006699',
  shared_order_background_color: 'white',
  shared_order_logo_url: '/assets/login-logo.png',
  button_background_color: '#006699',
  button_text_color: 'white',
};

const compile_branding = (host, branding) => {
  let compiled = default_branding;
  if (!!branding) compiled = branding;
  let logo_url = '/assets/notifications/ic_logo.svg';
  if (!!branding && !!branding.logo_url) logo_url = branding.logo_url;

  return `
    :root {
      --logo-url: url(${host}${logo_url});

      --orderer-background-color: ${compiled.orderer_background_color};
      --orderer-foreground-color: ${compiled.orderer_foreground_color};
      --orderer-background-image-url: url(${host}${compiled.orderer_background_image_url});

      --list-item-color: ${compiled.list_item_color};

      --worker-background-color: ${compiled.worker_background_color};
      --worker-foreground-color: ${compiled.worker_foreground_color};
      --worker-background-image-url: url(${host}${compiled.worker_background_image_url});

      --home-hexa-pilot: ${compiled.home_hexa_pilot};
      --home-hexa-apps: ${compiled.home_hexa_apps};
      --home-hexa-ongoing: ${compiled.home_hexa_ongoing};
      --home-hexa-pilot-hover: ${compiled.home_hexa_pilot_hover};
      --home-hexa-apps-hover: ${compiled.home_hexa_apps_hover};
      --home-hexa-ongoing-hover: ${compiled.home_hexa_ongoing_hover};

      --shared-order-header-color: ${compiled.shared_order_header_color};
      --shared-order-header-line-color: ${compiled.shared_order_header_line_color};
      --shared-order-background-color: ${compiled.shared_order_background_color};
      --shared-order-logo-url: url(${host}${compiled.shared_order_logo_url});
      --button_background_color: ${compiled.button_background_color};
      --button_text_color: ${compiled.button_text_color};
    }
  `;
};

const inject_style = (branding, host) => {
  const style_id = 'wp-branding-root';
  const current_style_el = document.querySelector('#' + style_id);
  const style = document.createElement('style');
  const content = compile_branding(host, branding);

  if (current_style_el) current_style_el.remove();

  style.id = style_id;
  style.textContent = content;

  document.head.append(style);

  // remove background flickering caused by css --var:
  if (typeof session === 'undefined') {
    console.log('No session.value');
  } else {
    // if (session?.value()?.user?.last_role === 'orderer') {
    //   document.getElementById('application').style.backgroundImage = `url(${host}${branding.orderer_background_image_url})`;
    // } else {
    //   document.getElementById('application').style.backgroundImage = `url(${host}${branding.worker_background_image_url})`;
    // }
  }

  // Events.dispatch('wp:branding:style', {
  //   orderer_background_image_url: `${host}${branding.orderer_background_image_url}`,
  //   worker_background_image_url: `${host}${branding.worker_background_image_url}`
  // });
  // background-image 2s ease-in-out;
};

const inject_branding = async service_id => {
  const host = settings.api_host;
  const result = await APIClient.get_merchant_style(service_id);
  let branding = default_branding;
  if (!!result) branding = result;

  session.write({ branding: { service_id, vars: branding } });
  inject_style(branding, host);
};

// ? Checked and verified api requests //
// general api requests ---------------------------------------------------- //
const get_config = async success_fn => {
  const result = await api_request({
    endpoint: '/config',
    authenticated: false,
  });

  const merchants_response = await get_merchants();

  result.merchants = merchants_response.merchants;

  session.write({ api_config: result });

  if (typeof success_fn === 'function' && result) return success_fn(result);
  return result;
};

const get_merchant_config = async data => {
  return await api_request({
    endpoint: `/config/merchant?merchant_uuid=${data.merchant_uuid}`,
    authenticated: false,
  });
};

const get_merchants = async () => {
  return await api_request({
    endpoint: '/available_merchants',
    authenticated: false,
  });
};

const get_merchants_for_user = async () => {
  return await api_request({
    endpoint: '/merchants_for_registering',
  });
};

// authentication/session ---------------------------------------------------------- //

const reload_session_data = async token => {
  if (token) {
    session.write({ token });
    console.log('getconfig 1')
    const api_config = await get_config();

    const profile_response = await api_request({ endpoint: '/profile' });
    const user = profile_response.user;
    session.write({
      language: user.language || 'en',
      role: user.last_role,
    });

    const merchants_response = await get_merchants_for_user();
    api_config.available_merchants = merchants_response.merchants;
    session.write({ api_config: api_config });

    const merchant = await get_merchant_config({
      merchant_uuid: user.merchant_uuid,
    });
    session.write({ merchant: merchant });

    let tutorial_result = await get_tutorial_result();
    session.write({ tutorial: tutorial_result });

    await inject_branding(session.value().merchant.merchant_uuid);
    init_translations(session);
  }
};

// tutorial ---------------------------------------------------------------- //

const get_tutorial_result = async () => {
  const tutorial = await get_tutorial();
  let tutorial_result = false;
  if (tutorial?.tutorials.length > 0) {
    tutorial_result = true;
  } else {
    // console.log('!!probably tutorial var has been nullified in get_profile()');
  }
  return tutorial_result;
};

// workers map ------------------------------------------------------------- //

const get_workers_for = async merchant_uuid => {
  return await api_request({
    endpoint: `/map/workers/${merchant_uuid}`,
    authenticated: false,
  });
};

// branding ---------------------------------------------------------------- //

const get_merchant_style = async merchant_uuid => {
  const result = await api_request({
    endpoint: '/merchant_style',
    method: 'GET',
    data: { merchant_uuid: merchant_uuid },
    authenticated: false,
  });

  // debugger
  return result;
};

const get_merchant_style_by_subdomain = async subdomain => {
  return await api_request({
    endpoint: '/merchant_by_subdomain',
    method: 'GET',
    data: { subdomain: subdomain },
    language: 'en',
  });
};

// payments ---------------------------------------------------------------- //

const init_pay_by_card = async data => {
  return await api_request({
    endpoint: '/payment/pay_by_card_init',
    method: 'POST',
    data,
  });
};

const finalize_pay_by_card = async data => {
  return await api_request({
    endpoint: '/payment/pay_by_card_finalize',
    method: 'POST',
    data,
  });
};

const init_reserve_card = async data => {
  return await api_request({
    endpoint: '/payment/reserve_card_init',
    method: 'POST',
    data,
  });
};

const finalize_reserve_card = async data => {
  return await api_request({
    endpoint: '/payment/reserve_card_finalize',
    method: 'POST',
    data,
  });
};

const get_payment_options = async data => {
  return await api_request({ endpoint: '/payment/options', data });
};

const get_payment_options_with_succ = async (data, success_fn) => {
  let result = await api_request({ endpoint: '/payment/options', data });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const init_card = async () => {
  return await api_request({ endpoint: '/payment/init_card' });
};

const finalize_card = async data => {
  return await api_request({
    endpoint: '/payment/finalize_card',
    method: 'POST',
    data,
  });
};

const remove_card = async data => {
  return await api_request({
    endpoint: '/payment/delete_card',
    method: 'POST',
    data,
  });
};

const decode_seita_error = async error_code => {
  return await api_request({
    endpoint: `/payment/seita_code_to_resource_id/${error_code}`,
  });
};

// notifications ----------------------------------------------------------- //

const get_notifications = async (params, success_fn) => {
  const result = await api_request({
    endpoint: '/notifications',
    data: params,
  });

  if (typeof success_fn === 'function' && result) return success_fn(result);
  return result;
};

const read_notifications = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/read_notifications',
    method: 'POST',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

// authentication/session ---------------------------------------------------------- //

const submit_login = async data => {
  const result = await api_request({
    endpoint: '/login',
    method: 'POST',
    data,
    max_retries: 0,
  });

  if (result) {
    let new_config = {
      ...result,
    };

    session.start(new_config);
    window.location.pathname = '/';
  }
};

const submit_logout = async () => {
  await api_request({
    endpoint: '/logout',
    method: 'POST',
    versioned: false,
    max_retries: 0,
  });

  session.destroy();
  broadcast_reload_message(session.value().token);
  window.location.redirect('/login');
};

// other auth related ------------------------------------------------------ //

const submit_forgotten_password = async (data, success_fn) => {
  let result = await api_request({
    endpoint: '/forgot_password',
    method: 'POST',
    data,
    max_retries: 0,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const change_password = async data => {
  return await api_request({
    endpoint: '/change_password',
    method: 'PUT',
    data,
    max_retries: 0,
  });
};

const reset_password = async data => {
  return await api_request({
    endpoint: '/reset_password',
    method: 'POST',
    data,
    authenticated: false,
    max_retries: 0,
  });
};

const submit_registration = async (data, success_fn) => {
  // try{
  const result = await api_request({
    endpoint: '/register',
    method: 'POST',
    data,
    max_retries: 0,
    handle_errors: false,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
  // } catch (e) {
  //   console.log('e', e);
  // }
};

const submit_resend_confirmation_email = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/resend_confirm_email',
    method: 'POST',
    data,
    max_retries: 0,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const switch_role = async data => {
  const result = await api_request({
    endpoint: '/profile',
    method: 'PUT',
    data,
    max_retries: 0,
  });

  if (result?.user) {
    let tutorial_result = await get_tutorial_result();
    session.write({
      user: result.user,
      tutorial: tutorial_result,
    });

    broadcast_reload_message(session.value().token);
  }

  return result;
};

const register_for_merchant = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/register_for_merchant',
    method: 'POST',
    data,
    max_retries: 0,
  });

  await get_profile();

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

// documents ------------------------------------------------------ //

const upload_document = async data => {
  await api_request({
    endpoint: '/profile/document',
    method: 'POST',
    data,
    max_retries: 0,
  });

  const response = await api_request({ endpoint: '/profile' });

  if (response) {
    const user = response.user;
    session.write({ user: user });
    return response;
  }
};

const delete_document = async data => {
  await api_request({
    endpoint: '/profile/document',
    method: 'DELETE',
    data,
    max_retries: 0,
  });
  const response = await api_request({ endpoint: '/profile' });

  if (response) {
    const user = response.user;
    session.write({ user: user });
    return response;
  }
};

const edit_document = async data => {
  await api_request({
    endpoint: '/profile/document',
    method: 'PUT',
    data,
    max_retries: 0,
  });

  const response = await api_request({ endpoint: '/profile' });

  if (response) {
    const user = response.user;
    session.write({ user: user });
    return response;
  }
};

// user -------------------------------------------------------------------- //

const update_profile = async data => {
  const response = await api_request({
    endpoint: '/profile',
    method: 'PUT',
    data,
  });

  if (response) {
    let user = response.user;
    let tutorial_result = await get_tutorial_result();

    session.write({
      language: user.language || 'en',
      role: user.last_role,
      user: user,
      tutorial: tutorial_result,
    });

    broadcast_reload_message(session.value().token);
    return user;
  }
};

const get_merchant_for_code = async private_code => {
  try {
    return await api_request({
      endpoint: '/merchant_for_code',
      data: { code: private_code },
      handle_errors: false,
    });
  } catch (e) {
    console.log(e);
  }
};

const get_feedbacks = async (role, data) => {
  return await api_request({ endpoint: '/profile/feedbacks/' + role, data });
};

const get_orderer_feedbacks = async data => {
  return await api_request({ endpoint: '/order/feedbacks', data });
};

const get_worker_feedbacks = async data => {
  return await api_request({ endpoint: '/order/feedbacks', data });
};

const get_applicant_feedbacks = async data => {
  return await api_request({ endpoint: '/order/feedbacks', data });
};

const get_worker_cv = async (worker_id, language) => {
  return await api_request({
    endpoint: '/cv/worker/' + worker_id,
    headers: { 'Accept-Language': language },
    authenticated: false,
  });
};

const delete_account = async () => {
  return await api_request({
    endpoint: '/profile',
    method: 'DELETE',
  });
};

const update_profile_without_reload = async data => {
  const response = await api_request({
    endpoint: '/profile',
    method: 'PUT',
    data,
  });

  if (response) {
    let user = response.user;
    return user;
  }
};

// validation -------------------------------------------------------------- //

const confirm_email = async data => {
  return await api_request({
    endpoint: '/confirm_email',
    data,
    authenticated: false,
  });
};

const verify_email = async data => {
  try {
    await api_request({
      endpoint: '/verify_email',
      method: 'POST',
      data,
      handle_errors: false,
      max_retries: 0,
    });
    return null;
  } catch (error) {
    return t9n(error.message);
  }
};

const verify_password = async data => {
  try {
    return await api_request({
      endpoint: '/verify_password',
      method: 'POST',
      data,
      handle_errors: false,
      max_retries: 0,
    });
  } catch (error) {
    return t9n(error.message);
  }
};

const verify_email_worker = async data => {
  try {
    const result = await api_request({
      endpoint: '/order/verify_email',
      method: 'POST',
      data,
      handle_errors: false,
      max_retries: 0,
    });

    return result;
  } catch (error) {
    return error;
  }
};

const verify_phone = async data => {
  try {
    await api_request({
      endpoint: '/verify_phone',
      method: 'POST',
      data,
      handle_errors: false,
      max_retries: 0,
    });
    return null;
  } catch (error) {
    return t9n(error.message);
  }
};

const verify_order_email_raw = async data => {
  return await api_request({
    endpoint: '/order/verify_email',
    method: 'post',
    data,
    handle_errors: false,
    max_retries: 0,
  });
};

// orders ------------------------------------------------------------------ //

const create_order = async data => {
  return await api_request({ endpoint: '/order', method: 'POST', data });
};

const edit_order = async data => {
  return await api_request({ endpoint: '/order', method: 'PUT', data });
};

const hide_order_on_cv = async data => {
  return await api_request({ endpoint: '/hide_on_cv', method: 'PUT', data });
};

const get_certificate = async (order_uuid, include_fb) => {
  return await api_request({
    endpoint: `/certificates/order_completed/${order_uuid}/${include_fb}`,
    data_type: 'url',
  });
};

const get_order_shared = async order_uuid => {
  return await api_request({
    endpoint: `/order/shared/${order_uuid}`,
    authenticated: false,
  });
};

const get_order_payment_details = async order_uuid => {
  return await api_request({
    endpoint: `/payment/order_paid/${order_uuid}`,
    data_type: 'url',
  });
};

const edit_order_with_apps = async data => {
  return await api_request({ endpoint: '/order', method: 'PATCH', data });
};

const get_orders = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/get_orders',
    method: 'POST',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const get_order = async (data, success_fn) => {
  const result = await api_request({ endpoint: '/order', data });
  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const get_order_with_errors = async data => {
  return await api_request({ endpoint: '/order', data, handle_errors: false });
};

const cancel_order = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/cancel_order',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const complete_task = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/complete_the_task',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const update_delivered_task = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/update_delivered_task',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const apply_for_order = async (data, success_fn) => {
  const result = await api_request({ endpoint: '/apply', method: 'PUT', data });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const approve_order = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/approve_order',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const cancel_application = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/cancel_application',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const select_worker = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/select_the_worker',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const update_worker_feedback = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/update_worker_feedback',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const update_orderer_feedback = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/update_orderer_feedback',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const hide_application = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/hide_application',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const show_application = async (data, success_fn) => {
  const result = await api_request({
    endpoint: '/show_application',
    method: 'PUT',
    data,
  });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

// PilotPoints ------------------------------------------------------------- //

const init_buy_pilot_points = async data => {
  return await api_request({
    endpoint: '/pilot_points/buy_init',
    method: 'POST',
    data,
  });
};

const finalize_buy_pilot_points = async data => {
  return await api_request({
    endpoint: '/pilot_points/buy_finalize',
    method: 'POST',
    data,
  });
};

const claim_pilot_points = async data => {
  return await api_request({
    endpoint: '/pilot_points/claim',
    method: 'POST',
    data,
  });
};

const donate_pilot_points = async data => {
  return await api_request({
    endpoint: '/pilot_points/donate',
    method: 'POST',
    data,
  });
};

const pilot_points_history = async data => {
  return await api_request({ endpoint: '/pilot_points/history', data });
};

// reports ----------------------------------------------------------------- //

const get_orderer_report_url = async (data, file_type) => {
  let url = `/orderer_order_report.${file_type}`;

  return await api_request({
    endpoint: url,
    data: data,
  });
};

const get_worker_report_url = async data => {
  return await api_request({
    endpoint: '/worker_report.pdf',
    data: data,
    data_type: 'url',
  });
};

const get_completed_task_report_url = async data => {
  return await api_request({
    endpoint: '/completed_tasks_report.pdf',
    data: data,
    data_type: 'url',
  });
};

// teams ------------------------------------------------------------------- //

const get_public_teams = async (data, success_fn) => {
  const result = await api_request({ endpoint: '/public_teams', data });

  if (typeof success_fn === 'function' && result) success_fn(result);
  return result;
};

const get_private_team = async (data, success_fn, failure_fn) => {
  try {
    const result = await api_request({
      endpoint: '/private_team',
      data,
      handle_errors: false,
    });

    if (typeof success_fn === 'function' && result) success_fn(result);
    return result;
  } catch (error) {
    if (typeof failure_fn === 'function') failure_fn(error);
    return error;
  }
};

const get_orderer_teams = async () => {
  return await api_request({ endpoint: '/teams' });
};

const get_team_users = async team_id => {
  return await api_request({
    endpoint: '/team/users/',
    data: { team_id: team_id },
  });
};

const add_team_user = async data => {
  return await api_request({ endpoint: '/team/user/', method: 'POST', data });
};

const remove_team_user = async data => {
  return await api_request({ endpoint: '/team/user/', method: 'DELETE', data });
};

const add_team = async data => {
  return await api_request({ endpoint: '/teams', method: 'POST', data });
};

const delete_team = async data => {
  return await api_request({ endpoint: '/teams', method: 'DELETE', data });
};

// chat -------------------------------------------------------------------- //

const get_chat_messages = async data => {
  return await api_request({ endpoint: '/chats', data });
};

const read_chat_messages = async data => {
  return await api_request({ endpoint: '/read_chats', method: 'POST', data });
};

const send_chat_message = async data => {
  return await api_request({
    endpoint: '/send_chat_message',
    method: 'POST',
    data,
  });
};

const send_chat_image = async data => {
  return await api_request({
    endpoint: '/send_chat_image',
    method: 'POST',
    data,
  });
};

const get_chat_summary = async data => {
  return await api_request({ endpoint: '/chats/summary', data });
};

// invite ------------------------------------------------------------------ //

const submit_invite = async data => {
  return await api_request({
    endpoint: '/invite',
    method: 'POST',
    data,
    max_retries: 0,
  });
};

const get_invite_token = async token => {
  return await api_request({
    endpoint: `/invite/${token}`,
    method: 'GET',
    authenticated: false,
  });
};

// tutorial ---------------------------------------------------------------- //

const get_tutorial = async data => {
  return await api_request({ endpoint: '/tutorials', method: 'GET', data });
};

const put_tutorial = async data => {
  return await api_request({
    endpoint: '/tutorials',
    method: 'PUT',
    data,
  });
};

// billing ---------------------------------------------------------------- //

const post_billing_request = async data => {
  return await api_request({
    endpoint: '/billing_request',
    method: 'POST',
    data,
  });
};

const last_billing_request = async () => {
  return await api_request({
    endpoint: '/last_billing_request',
    method: 'GET',
  });
};

// # Under check //
// general api requests ---------------------------------------------------- //

const switch_to_merchant = async (data, url = null) => {
  const result = await api_request({
    endpoint: '/switch_to_merchant',
    method: 'POST',
    data,
    max_retries: 0,
  });

  // ! Norbi //
  const merchant = await get_merchant_config({
    merchant_uuid: result.user.merchant_uuid,
  });

  const subdomain = merchant.subdomain
    ? merchant.subdomain
    : settings.default_subdomain;
  if (subdomain !== subdomain_from(window.location.host)) {
    const subdomain_url = url_for(settings, subdomain);

    // send_reload_message_to(subdomain_url, result.token);

    if (!window.location.origin.includes(subdomain)) {
      // session.destroy();
      window.location.assign(subdomain_url);
    }
    // !  //
  } else {
    session.write({ token: result.token });
    let tutorial_result = await get_tutorial_result();

    session.write({
      user: result.user,
      merchant: merchant,
      tutorial: tutorial_result,
    });

    await inject_branding(result.user.merchant_uuid);
    broadcast_reload_message(result.token);

    if (!!url) {
      router.navigate(`${url}&group=0&page=1`);
    } else {
      router.navigate('/home?switch=true');
      // window.location.assign('/home');
    }
  }
};

// ! Not checked yet //

// user -------------------------------------------------------------------- //

const get_profile = async () => {
  const response = await api_request({ endpoint: '/profile' });

  if (response) {
    const user = response.user;
    session.write({ user: user });
    // session.value().tracker.setUserID(user.email);
    console.log('getconfig 3')
    const api_config = await get_config();
    const merchants_response = await get_merchants_for_user();
    api_config.available_merchants = merchants_response.merchants;
    session.write({ api_config: api_config });

    const merchant = await get_merchant_config({
      merchant_uuid: response.user.merchant_uuid,
    });
    session.write({ merchant: merchant });

    let tutorial_result = await get_tutorial_result();
    session.write({ tutorial: tutorial_result });

    return user;
  }
};

// # Non used requests //
const get_applicant_profile = async data => {
  return await api_request({ endpoint: '/order/applicant_profile', data });
};

const verify_order_email = async (data, success_fn, failure_fn) => {
  try {
    const result = await api_request({
      endpoint: '/order/verify_email',
      method: 'POST',
      data,
      handle_errors: false,
      max_retries: 0,
    });
    if (typeof success_fn === 'function' && result) return success_fn(result);
    return result;
  } catch (error) {
    if (typeof failure_fn === 'function') return failure_fn(error);
    return error;
  }
};

//only used in buy.test.js
const buy_pilot_points = async data => {
  return await api_request({
    endpoint: '/pilot_points/buy',
    method: 'POST',
    data,
  });
};

// board -------------------------------------------------------------------- //

const get_board_participants = async order_id => {
  return await api_request({
    endpoint: '/chats/applicants',
    data: { order_uuid: order_id },
  });
};

const get_board_messages = async (order_id, applicant_id) => {
  return await api_request({
    endpoint: `/chats/applicant/${applicant_id}`,
    data: { order_uuid: order_id },
  });
};

const read_board_messages = async data => {
  return await api_request({ endpoint: '/read_chats', method: 'POST', data });
};

const send_board_message = async data => {
  return await api_request({
    endpoint: '/send_chat_message/applicant',
    method: 'post',
    data,
  });
};

// admin -------------------------------------------------------------------- //

const get_admin_table = async data => {
  const defaults = {
    order_by: 'id',
    order_by_type: 'descending',
    filters: encodeURIComponent(JSON.stringify({})),
    limit: 100,
  };

  const request_data = mergeDeepRight(defaults, data);

  return await api_request({
    endpoint: '/admin/table',
    method: 'GET',
    data: request_data,
    versioned: false,
  });
};

const get_admin_table_row = async (table_name, row_id) => {
  const data = { table_name, row_id };

  return await api_request({
    endpoint: '/admin/row',
    method: 'GET',
    data,
    versioned: false,
  });
};

const new_admin_table_row = async (table_name, merchant_uuid) => {
  const data = { table_name, defaults: { merchant_uuid } };

  return await api_request({
    endpoint: '/admin/row/new',
    method: 'POST',
    data,
    versioned: false,
  });
};

const add_admin_table_row = async (table_name, row) => {
  const data = { table_name, row, is_new: true };

  return await api_request({
    endpoint: '/admin/row',
    method: 'PUT',
    data,
    versioned: false,
  });
};

const update_admin_table_row = async (table_name, row) => {
  const data = { table_name, row, is_new: false };

  return await api_request({
    endpoint: '/admin/row',
    method: 'PUT',
    data,
    versioned: false,
  });
};

const delete_admin_table_row = async (table_name, row_id) => {
  const data = { table_name, row_id };

  return await api_request({
    endpoint: '/admin/row',
    method: 'DELETE',
    data,
    versioned: false,
  });
};

// handlers ---------------------------------------------------------------- //

const start_handling_unauthenticated_requests = e => {
  console.log(e);
  session.destroy();
  window.location.pathname = '/login';
};

// lifecycle --------------------------------------------------------------- //

const init = () => {
  document.addEventListener(
    'wp:api:unauthenticated',
    start_handling_unauthenticated_requests
  );
};

const destroy = () => {
  document.removeEventListener(
    'wp:api:unauthenticated',
    start_handling_unauthenticated_requests
  );
};

const APIClient = {
  // branding
  inject_style,
  inject_branding,

  // lifecycle
  init,
  destroy,

  // data
  unauthorized_endpoints,

  // config data
  get_config,
  get_merchants,
  get_merchant_config,

  // workers map
  get_workers_for,

  // branding
  get_merchant_style,
  get_merchant_style_by_subdomain,

  // payments
  init_pay_by_card,
  finalize_pay_by_card,
  init_reserve_card,
  finalize_reserve_card,
  get_payment_options,
  get_payment_options_with_succ,
  init_card,
  finalize_card,
  remove_card,
  decode_seita_error,

  // notifications
  get_notifications,
  read_notifications,

  // authentication and session
  reload_session_data,
  submit_login,
  submit_logout,
  submit_forgotten_password,
  change_password,
  reset_password,
  submit_registration,
  submit_resend_confirmation_email,
  switch_role,
  register_for_merchant,
  switch_to_merchant,
  confirm_email,

  // user and profile
  get_merchant_for_code,
  get_profile,
  update_profile,
  update_profile_without_reload,
  get_feedbacks,
  get_orderer_feedbacks,
  get_worker_feedbacks,
  get_applicant_feedbacks,
  get_applicant_profile,
  get_worker_cv,
  delete_account,

  // validation
  verify_email,
  verify_phone,
  verify_email_worker,
  verify_order_email,
  verify_order_email_raw,
  verify_password,

  // orders
  create_order,
  edit_order,
  hide_order_on_cv,
  get_certificate,
  get_order_shared,
  get_order_payment_details,
  edit_order_with_apps,
  get_orders,
  get_order,
  get_order_with_errors,
  cancel_order,
  complete_task,
  update_delivered_task,
  apply_for_order,
  approve_order,
  cancel_application,
  select_worker,
  update_orderer_feedback,
  hide_application,
  show_application,
  update_worker_feedback,

  // pilotpoints
  buy_pilot_points,
  init_buy_pilot_points,
  finalize_buy_pilot_points,
  claim_pilot_points,
  donate_pilot_points,
  pilot_points_history,

  // reports
  get_orderer_report_url,
  get_worker_report_url,
  get_completed_task_report_url,

  // teams
  get_public_teams,
  get_private_team,
  get_orderer_teams,
  get_team_users,
  add_team_user,
  remove_team_user,
  add_team,
  delete_team,

  // chat
  get_chat_messages,
  read_chat_messages,
  send_chat_message,
  send_chat_image,
  get_chat_summary,

  // board
  get_board_participants,
  get_board_messages,
  send_board_message,
  read_board_messages,

  // admin
  get_admin_table,
  get_admin_table_row,
  new_admin_table_row,
  add_admin_table_row,
  update_admin_table_row,
  delete_admin_table_row,

  // documents
  upload_document,
  edit_document,
  delete_document,

  // invite
  submit_invite,
  get_invite_token,

  // tutorial
  get_tutorial,
  put_tutorial,

  //billing
  post_billing_request,
  last_billing_request,
};

export default APIClient;
